import type { SVGAttributes } from 'react';
import type { WeightStatus } from '@/logic/weights';
import { cn } from './cn';

const DEFAULT_TONE: Record<WeightStatus, string> = {
  unity: 'text-fg-muted',
  fixed: 'text-accent',
  free: 'text-fg-muted',
  ignore: 'text-fg-subtle',
};

export interface WeightGlyphProps extends Omit<
  SVGAttributes<SVGSVGElement>,
  'children'
> {
  status: WeightStatus;
  /** Rendered size in px. */
  size?: number;
}

/**
 * Weight encoded by shape, never by status color: Unity ● filled circle,
 * Fixed ▲ filled triangle (accent), Free ◌ dashed hollow circle,
 * Ignore × (muted). Decorative: always pair it with the weight's label.
 * The map draws the same shapes; see docs/developer/design-system.md.
 */
export const WeightGlyph = ({
  status,
  size = 12,
  className,
  ...props
}: WeightGlyphProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 12 12"
    aria-hidden="true"
    className={cn('shrink-0', DEFAULT_TONE[status], className)}
    {...props}
  >
    {status === 'unity' && (
      <circle cx="6" cy="6" r="4.25" fill="currentColor" />
    )}
    {status === 'fixed' && (
      <path d="M6 1.5 10.75 10H1.25Z" fill="currentColor" />
    )}
    {status === 'free' && (
      <circle
        cx="6"
        cy="6"
        r="4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="2.1 1.6"
      />
    )}
    {status === 'ignore' && (
      <path
        d="M2.75 2.75 9.25 9.25M9.25 2.75 2.75 9.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    )}
  </svg>
);
